import { Modal } from '@odin-labs/components';
import React from 'react';
import type { PublishChecks } from 'siteLayout';

export type PublishDraftModalProps = {
  isOpen: boolean;
  siteName: string;
  nextRevisionNumber: number;
  currentRevisionNumber: number | null;
  checks: PublishChecks;
  publishing: boolean;
  onCancel: () => void;
  onPublish: () => void;
  onOpenItem: (type: 'zone' | 'entry', id: string) => void;
};

const classes = {
  body: 'odin-flex odin-flex-col odin-gap-4',
  label: (danger: boolean): string =>
    `odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide ${
      danger ? 'odin-text-red-600' : 'odin-text-gray-500'
    }`,
  blocker:
    'odin-flex odin-flex-col odin-gap-1 odin-rounded-md odin-bg-red-50 odin-px-3 odin-py-2 odin-text-sm odin-text-gray-900',
  warning:
    'odin-flex odin-flex-col odin-gap-1 odin-rounded-md odin-bg-yellow-50 odin-px-3 odin-py-2 odin-text-sm odin-text-gray-900',
  names: 'odin-text-xs odin-text-gray-600',
  link: 'odin-self-start odin-text-xs odin-font-medium odin-text-odin-primary hover:odin-underline',
  clean: 'odin-text-sm odin-text-gray-500',
  confirm: 'odin-text-xs odin-text-gray-500',
};

/** What publishing this draft would do, and what stands in its way. */
export function PublishDraftModal({
  isOpen,
  siteName,
  nextRevisionNumber,
  currentRevisionNumber,
  checks,
  publishing,
  onCancel,
  onPublish,
  onOpenItem,
}: PublishDraftModalProps): React.ReactElement {
  return (
    <Modal
      open={isOpen}
      setOpen={(open) => !open && onCancel()}
      title={`Publish revision ${nextRevisionNumber}`}
      subtitle={siteName}
      size="md"
      actionText={`Publish revision ${nextRevisionNumber}`}
      actionButtonEnabled={checks.canPublish && !publishing}
      actionButtonWithSpinner={publishing}
      onAction={onPublish}
      cancelText="Cancel"
      onCancel={onCancel}
    >
      <div className={classes.body}>
        {checks.blocking.length > 0 && (
          <>
            <p className={classes.label(true)}>Blocking</p>
            {checks.blocking.map((blocker) => (
              <div key={blocker.text} className={classes.blocker}>
                <span>{blocker.text}</span>
                {blocker.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={classes.link}
                    onClick={() => onOpenItem(item.type, item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
        {checks.warnings.length > 0 && (
          <>
            <p className={classes.label(false)}>Warnings</p>
            {checks.warnings.map((warning) => (
              <div key={warning.text} className={classes.warning}>
                <span>{warning.text}</span>
                {warning.names && (
                  <span className={classes.names}>
                    {warning.names.join(', ')}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
        {checks.blocking.length === 0 && checks.warnings.length === 0 && (
          <p className={classes.clean}>Nothing to flag.</p>
        )}
        <p className={classes.confirm}>
          Publishing makes this layout effective now
          {currentRevisionNumber
            ? ` and closes revision ${currentRevisionNumber}`
            : ''}
          . When the site&apos;s zones mode is Shadow or Enforce and its
          Avigilon connector is enabled, Odin then syncs the layout to Avigilon
          separately. Check the sync result before testing a reader.
        </p>
        <p className={classes.confirm}>
          Shadow creates and names zones and access groups, and an entry for
          each entry side readers admit into, with the exception below. It sets
          each access group&apos;s zones, granting the default group every zone,
          removes archived access groups, and syncs site worker memberships, but
          never changes reader attachments or reader direction. An access group
          adopted from Avigilon keeps its name, and an archived one is emptied
          rather than removed.
        </p>
        <p className={classes.confirm}>
          Enforce also limits the default group to its own zones, moves entries
          between zones, attaches and detaches readers, and sets reader
          direction.
        </p>
        <p className={classes.confirm}>
          In either mode, Odin creates no Avigilon entry for an Identify only
          side with a reader linked in Avigilon. In Enforce, that keeps the
          Avigilon sync incomplete until the side is Enforced or no reader
          linked in Avigilon admits into it.
        </p>
      </div>
    </Modal>
  );
}
